import { Injectable } from '@angular/core';
import { UserDataService } from './user-data.service';
import { recentSearchesKey } from '../constants/constants';

interface RecentSearchesLocalStorage {
  [key: string]: string[][];
}

@Injectable({
  providedIn: 'root',
})
export class RecentSearchService {
  maxNumberOfSearches = 5;

  constructor(private userDataService: UserDataService) {}

  /**
   * The function retrieves recent searches from local storage and returns them as an object.
   * @returns the value of the "searches" variable, which is either the parsed value of the
   * "recentSearchesKey" item in the localStorage or an empty object if the item does not exist.
   */
  listSearches(): RecentSearchesLocalStorage {
    const searches: RecentSearchesLocalStorage | undefined = JSON.parse(localStorage.getItem(recentSearchesKey));
    if (!searches) {
      return {};
    }
    return searches;
  }

  /**
   * The function "listUserSearches" returns an array of recent searches made by the current user,
   * stored in local storage.
   * @returns The function `listUserSearches` returns a two-dimensional array of strings.
   */
  listUserSearches(): string[][] {
    const searches = this.listSearches();
    const userId = this.userDataService.user.id;

    // if searches is empty objetc
    if (Object.keys(searches).length === 0) {
      // add empty array to local storage
      localStorage.setItem(
        recentSearchesKey,
        JSON.stringify({
          [userId]: [],
        }),
      );
      return [];
    }

    if (Object.keys(searches).length > 0) {
      //search if user id exists as field
      const userSearches = searches[userId];
      if (!userSearches) {
        localStorage.setItem(
          recentSearchesKey,
          JSON.stringify({
            [userId]: [],
            ...searches,
          }),
        );
        return [];
      }

      if (userSearches) {
        return userSearches;
      }
    }

    return [];
  }

  /**
   * The function `addUserSearch` adds a user's search terms to their list of recent searches, ensuring
   * that the list does not exceed the maximum number of searches allowed.
   * @param {string[]} searchTerms - An array of strings representing the search terms entered by the
   * user.
   */
  addUserSearch(searchTerms: string[]): void {
    const userSearches = [...this.listUserSearches()];
    const userId = this.userDataService.user.id;

    //find searchTerms if it is repeated
    const index = userSearches.findIndex((search) => {
      const sortedSearchTerms = [...searchTerms].sort();
      const sortedSearch = [...search].sort();
      return JSON.stringify(sortedSearch) === JSON.stringify(sortedSearchTerms);
    });

    //delete if found
    if (index > -1) {
      userSearches.splice(index, 1);
    }

    if (userSearches.length >= this.maxNumberOfSearches) {
      userSearches.pop();
    }
    userSearches.unshift(searchTerms);

    const searches = this.listSearches();
    searches[userId] = userSearches;

    localStorage.setItem(recentSearchesKey, JSON.stringify(searches));
  }

  /**
   * The function `clearUserSearches` deletes the recent searches of the current user from local
   * storage.
   */
  clearUserSearches(): void {
    const userId = this.userDataService.user.id;
    const searches = this.listSearches();
    if (searches) {
      delete searches[userId];
      localStorage.setItem(recentSearchesKey, JSON.stringify(searches));
    }
  }
}
